import { useCallback, useState, FC, ReactNode, ReactEventHandler } from "react";
import { EmotionClassStyles } from "@/domains/emotion/types";
import {
  MdsDropdownContentList,
  MdsDropdownProps,
  MdsDropdown,
} from "@/design-system/components/dropdown";
import { MdsIconKind } from "@/design-system/components/icon";
import {
  IMDSButtonProps,
  MDSButton,
  MDSButtonIcon,
  MDSButtonSlotName,
  MDSButtonVariant,
} from "@/mds";
import { MDSTooltip, MDSTooltipConfig } from "@/mds/components/tooltip";

export interface MdsDropdownButtonProps extends EmotionClassStyles {
  innerStyles?: MdsDropdownProps["innerStyles"];
  contentList: MdsDropdownContentList;
  iconKind?: MdsIconKind;
  dropdownIcon?: boolean;
  label?: ReactNode;
  variant?: IMDSButtonProps["variant"];
  isDisabled?: IMDSButtonProps["isDisabled"];
  isLoading?: IMDSButtonProps["isLoading"];
  placement?: MdsDropdownProps["placement"];
  tooltipConfig?: MDSTooltipConfig;
  onOpen?: (isOpen: boolean) => void;
  offset?: MdsDropdownProps["offset"];
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsDropdownButton: FC<MdsDropdownButtonProps> = ({
  className,
  innerStyles,
  contentList,
  iconKind,
  dropdownIcon,
  label,
  isDisabled,
  isLoading,
  placement,
  tooltipConfig,
  onOpen,
  offset,
  variant = MDSButtonVariant.Default,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleIsOpenState = useCallback(() => {
    onOpen?.(!isOpen);
    setIsOpen(prevState => !prevState);
  }, [isOpen, onOpen]);

  const handleOpenChange = (isOpen: boolean) => {
    onOpen?.(isOpen);
    setIsOpen(isOpen);
  };

  const handleClick: ReactEventHandler = useCallback(
    event => {
      event.stopPropagation();
      toggleIsOpenState();
    },
    [toggleIsOpenState]
  );

  return (
    <MdsDropdown
      className={className}
      innerStyles={innerStyles}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      contentList={contentList}
      placement={placement}
      offset={offset}
    >
      <MDSTooltip config={tooltipConfig}>
        <MDSButton
          variant={variant}
          onClick={handleClick}
          isLoading={isLoading}
          isDisabled={isDisabled}
        >
          {iconKind && <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={iconKind} />}
          {label}
          {dropdownIcon && (
            <MDSButtonIcon
              slot={MDSButtonSlotName.RightIcon}
              kind={isOpen ? MdsIconKind.AngleUp : MdsIconKind.AngleDown}
            />
          )}
        </MDSButton>
      </MDSTooltip>
    </MdsDropdown>
  );
};
